// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { trackingNumberDetailsSchema } from '@/constants/shipping-carrier';
import { currencySchema } from '@/currency/api/types';
import { serializeOptionalCurrencyForAPI } from '@/currency/utilities/serialize';
import { Shipment, shipmentSchema } from '@/shipment/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { dateSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

/**
 * Schema for validating the response from the create shipment API endpoint.
 */
const createShipmentResponseSchema = z.object({
  data: shipmentSchema,
  metadata: responseMetadataSchema,
});

/**
 * Schema for shipment items in the request.
 * Uses unified itemId for both parts and products.
 */
const shipmentItemSchema = z.object({
  itemId: z.string().uuid(),
  quantity: z.number().int().min(0).nullable(),
  externalReference: z.string().optional().nullable(),
  serials: z.array(z.string()).optional(),
});

/**
 * Schema for validating the create shipment request.
 * Accepts lowercase shipment type from frontend, transforms to UPPERCASE for backend.
 */
export const createShipmentSchema = z.object({
  shipmentType: z.enum(['inbound', 'outbound']),
  purchaseOrderNumber: z.string().optional().nullable(),
  returnOrderNumber: z.string().optional().nullable(),
  fulfillmentOrderNumber: z.string().optional().nullable(),
  trackingNumberDetails: trackingNumberDetailsSchema.nullable().optional(),
  fromLocationId: z.string().uuid().optional().nullable(),
  toLocationId: z.string().uuid(),
  items: z.array(shipmentItemSchema).default([]),
  shippedAt: z.string().optional().nullable(),
  shippedAtTimezone: z.string().optional().nullable(),
  deliveredAt: z.string().optional().nullable(),
  deliveredAtTimezone: z.string().optional().nullable(),
  expectedShippedOn: dateSchema.optional().nullable(),
  expectedDeliveredOn: dateSchema.optional().nullable(),
  shipmentNote: z.string().optional().nullable(),
  shippingCost: currencySchema.nullable().optional(),
});

/**
 * Type definition for the create shipment request.
 */
type CreateShipmentRequest = z.infer<typeof createShipmentSchema>;

/**
 * Creates a new shipment with the specified details.
 *
 * @param request - The shipment details to create
 * @returns The created shipment
 */
export async function createShipment(
  request: CreateShipmentRequest
): Promise<Shipment> {
  const url = '/v1/shipment';
  const response = await api.post(url, {
    ...request,
    expectedShippedOn: serializeDateForAPI(request.expectedShippedOn),
    expectedDeliveredOn: serializeDateForAPI(request.expectedDeliveredOn),
    shippingCost: serializeOptionalCurrencyForAPI(request.shippingCost),
  });

  const { data } = await parseResponse(
    createShipmentResponseSchema,
    response.data,
    {
      schemaName: 'CreateShipment',
      endpoint: url,
    }
  );
  return data;
}
